import { profilesTrackingService } from "../../services";
import React, { useState, useEffect, useMemo } from "react";
import styled from "styled-components";
import {
  ModalOverlay,
  ModalContainer,
  ModalHeader,
  HeaderContent,
  HeaderIcon,
  HeaderText,
  ModalTitle,
  ModalSubtitle,
  CloseButton,
  TabsContainer,
  TabGroupTitle,
  EditorLayout,
  Tab,
  SectionHint,
  HoursToolbar,
  HoursChip,
  HoursHeader,
  HoursDayRow,
  HoursSwitch,
  HoursSlot,
  AddSlotButton,
  ModalBody,
  FormSection,
  SectionTitle,
  FormGrid,
  FormGroup,
  Label,
  Input,
  Select,
  TextArea,
  CharCounter,
  ModalFooter,
  FooterInfo,
  SaveFeedback,
  ButtonGroup,
  Button,
} from "./styles";
import { LocationPicker } from "./LocationPicker";
import { TimePickerCustom } from "./TimePickerCustom";
import Tooltip from "../../../../components/tooltip";
import { profileAdvisorService } from "../../services/profileAdvisorService";
import { seoProfileApi, type SeoResearch } from "../../services/seo";
import { Profile } from "./types";
import { useEditProfileForm } from "./hooks/useEditProfileForm";
import { hasNewRiskyPublishedValue, SAFE_PUBLISH_WARNING } from "./safe-publish-warning";
import { ServicesTab, ProductsTab, PostsTab, GabaritoTab, SeoTab,
  PresencaDigitalTab, InstagramTab,
} from "./components";
import { FieldSyncBadge } from "./components/FieldSyncBadge";
import { WhatsappNoGoogle } from "./components/WhatsappNoGoogle";
import { ConfirmacaoDoWhatsappDoCliente } from "./components/ConfirmacaoDoWhatsapp";
import { EncerrarImpulsionamento } from "./components/EncerrarImpulsionamento";
import {
  Aside,
  AsideCard,
  Badge,
  Botao,
  BotaoPequeno,
  CabecalhoDaSecao,
  Campo,
  Check,
  Chip,
  ChipBox,
  ChipBusca,
  ChipSugestao,
  ChipX,
  Coluna,
  Completude,
  EditorGrid,
  Hint,
  Lab,
  Ponto,
  Prefixo,
  PreviaNoGoogle,
  Tip,
} from "./components/EditorChrome";
import { ReviewsManager } from "../../../reviews/components/ReviewsManager";
import { useUserStore } from "../../../../store/user";
import {
  applyCategorySuggestion,
  getVisibleCategories,
} from "./category-options";
import {
  createKeywordDrafts,
  filterOfficialCategorySuggestions,
  getSelectedKeywordValues,
  removeKeywordDraft,
  sanitizeKeywordSuggestions,
  updateKeywordDraftSelection,
  updateKeywordDraftValue,
  type CategorySuggestion,
  type KeywordDraft,
} from "./assistant-suggestions";
import {
  buildCompleteGabarito,
  buildPublicBusinessDescription,
} from "./profile-content-suggestions";
import {
  buildAdvisorCategories,
  buildAdvisorKeywords,
  buildFormattedAddress,
} from "./assistant-context";
import {
  appendKeywords,
  readGabaritoState,
  writeGabaritoState,
} from "./components/gabarito-utils";
import {
  getServiceAreaOrigin,
} from "./service-area-suggestions";
import { fetchServiceAreaSuggestions } from "./service-area-remote";

const INSTAGRAM_TAB_ENABLED = import.meta.env.VITE_INSTAGRAM_INTEGRATION === "true";

// Componente para ícone de informação com tooltip
const InfoIcon = styled.span`
  font-size: 16px !important;
  color: #6b7280;
  cursor: help;
  transition: color 0.2s ease;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  margin-left: 6px;
  vertical-align: middle;

  &:hover {
    color: #1264C7;
  }
`;

// Re-exportar tipos para compatibilidade com código externo
export type { Profile } from "./types";

interface EditProfileModalProps {
  isOpen: boolean;
  profile: Profile | null;
  onClose: () => void;
  onSave: (updatedProfile: Profile) => Promise<void>;
  hideIATab?: boolean;
}

type ChaveDeAtributo = "accessibility" | "parking" | "payments" | "serviceOptions" | "amenities";

/** Grupos da aba "Mais atributos" (prancheta PerfilAtributos), com as mesmas opções de antes. */
const GRUPOS_DE_ATRIBUTOS: { chave: ChaveDeAtributo; nome: string; icone: string; itens: string[] }[] = [
  { chave: "accessibility", nome: "Acessibilidade", icone: "accessible", itens: ["Entrada com acessibilidade", "Estacionamento acessível", "Banheiro acessível", "Elevador acessível"] },
  { chave: "parking", nome: "Estacionamento", icone: "local_parking", itens: ["Estacionamento gratuito", "Estacionamento pago", "Estacionamento na rua", "Valet"] },
  { chave: "payments", nome: "Formas de pagamento", icone: "payment", itens: ["Cartões de crédito", "Cartões de débito", "Dinheiro", "Pix", "Pagamento por NFC", "Visa", "Mastercard", "American Express"] },
  { chave: "serviceOptions", nome: "Opções de serviço", icone: "room_service", itens: ["Atendimento presencial", "Entrega", "Retirada na loja", "Serviço online", "Drive-through"] },
  { chave: "amenities", nome: "Comodidades", icone: "star", itens: ["Wi-Fi gratuito", "Ar-condicionado", "Pet-friendly", "Área externa", "Brinquedoteca"] },
];
const TOTAL_DE_ATRIBUTOS = GRUPOS_DE_ATRIBUTOS.reduce((soma, grupo) => soma + grupo.itens.length, 0);

export const EditProfileModal: React.FC<EditProfileModalProps> = ({
  isOpen,
  profile,
  onClose,
  onSave,
  hideIATab = false,
}) => {
  const [activeTab, setActiveTab] = useState("basico");
  const loggedUser = useUserStore((state) => state.user);
  const isInternalUser = !!loggedUser && loggedUser.userType !== "CUSTOMER";
  const [loading, setLoading] = useState(false);
  const [saveFeedback, setSaveFeedback] = useState<string | null>(null);
  const [iaDescription, setIaDescription] = useState("");
  const [iaLoadingAction, setIaLoadingAction] = useState<
    "keywords" | "categories" | "briefing" | "commercial" | null
  >(null);
  const [commercialPlan, setCommercialPlan] = useState<SeoResearch | null>(
    null,
  );
  const [iaFeedback, setIaFeedback] = useState<{
    type: "success" | "error" | "info";
    message: string;
  } | null>(null);
  const [showMapPicker, setShowMapPicker] = useState(false);
  const [suggestedCategories, setSuggestedCategories] = useState<
    CategorySuggestion[]
  >([]);
  const [keywordDrafts, setKeywordDrafts] = useState<KeywordDraft[]>([]);
  const [autoFilledAreaProfileId, setAutoFilledAreaProfileId] = useState("");
  const [coverageAreaSearchQuery, setCoverageAreaSearchQuery] = useState("");
  const [coverageAreaRadiusKm, setCoverageAreaRadiusKm] = useState(15);
  const [serviceAreaSuggestions, setServiceAreaSuggestions] = useState<
    Awaited<ReturnType<typeof fetchServiceAreaSuggestions>>
  >([]);
  const [serviceAreaSuggestionsLoading, setServiceAreaSuggestionsLoading] =
    useState(false);
  const [primaryCategoriesSearchQuery, setPrimaryCategoriesSearchQuery] =
    useState("");
  const [categoriesSearchQuery, setCategoriesSearchQuery] = useState("");
  const [categoriesTouched, setCategoriesTouched] = useState(false);
  const [categoryFeedback, setCategoryFeedback] = useState("");
  const [grupoAberto, setGrupoAberto] = useState<ChaveDeAtributo | null>("accessibility");
  const [buscaAtributo, setBuscaAtributo] = useState("");

  // Usar novo hook para gerenciar form data e categorias
  const { 
    formData, 
    setFormData, 
    categories, 
    loadingCategories, 
    handleChange: handleFormChange 
  } = useEditProfileForm(profile, isOpen);

  const primaryCategoryValue = useMemo(
    () =>
      (formData?.categoryId &&
      categories.some((cat) => cat.gcid === formData.categoryId)
        ? formData.categoryId
        : categories.find(
            (cat) =>
              cat.category === formData?.category ||
              cat.category === formData?.categoryId,
          )?.gcid) || "",
    [categories, formData?.category, formData?.categoryId],
  );

  const visiblePrimaryCategories = useMemo(
    () =>
      getVisibleCategories(
        categories,
        primaryCategoriesSearchQuery,
        primaryCategoryValue ? [primaryCategoryValue] : [],
        [formData?.category, formData?.categoryId].filter(
          (value): value is string => Boolean(value),
        ),
      ),
    [
      categories,
      primaryCategoriesSearchQuery,
      primaryCategoryValue,
      formData?.category,
      formData?.categoryId,
    ],
  );

  const primaryCategoryOptions = useMemo(
    () =>
      visiblePrimaryCategories.map((cat) => (
        <option key={cat.gcid} value={cat.gcid}>
          {cat.category}
        </option>
      )),
    [visiblePrimaryCategories],
  );

  const visibleCategories = useMemo(
    () =>
      getVisibleCategories(
        categories,
        categoriesSearchQuery,
        formData?.secondaryCategoryIds || [],
        formData?.secondaryCategories || [],
      ),
    [
      categories,
      categoriesSearchQuery,
      formData?.secondaryCategoryIds,
      formData?.secondaryCategories,
    ],
  );

  const serviceAreaOrigin = useMemo(
    () =>
      getServiceAreaOrigin({
        city: formData?.city,
        address: formData?.address,
        latitude: formData?.latitude,
        longitude: formData?.longitude,
        state: formData?.state,
        serviceAreas: formData?.serviceAreas,
      }),
    [
      formData?.address,
      formData?.city,
      formData?.latitude,
      formData?.longitude,
      formData?.state,
      formData?.serviceAreas,
    ],
  );

  const selectedKeywordValues = useMemo(
    () => getSelectedKeywordValues(keywordDrafts),
    [keywordDrafts],
  );

  // Reset categoriesSearchQuery quando modal fecha
  useEffect(() => {
    if (!isOpen) {
      setShowMapPicker(false);
      setPrimaryCategoriesSearchQuery("");
      setCategoriesSearchQuery("");
      setCoverageAreaSearchQuery("");
      setCoverageAreaRadiusKm(15);
      setServiceAreaSuggestions([]);
      setServiceAreaSuggestionsLoading(false);
      setSuggestedCategories([]);
      setKeywordDrafts([]);
      setAutoFilledAreaProfileId("");
      setSaveFeedback(null);
      setCategoriesTouched(false);
      setCategoryFeedback("");
    }
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen || !formData?.id) {
      setServiceAreaSuggestions([]);
      return;
    }

    let active = true;
    setServiceAreaSuggestionsLoading(true);

    fetchServiceAreaSuggestions(formData.id, formData, {
      query: coverageAreaSearchQuery,
      radiusKm: coverageAreaRadiusKm,
    })
      .then((suggestions) => {
        if (active) setServiceAreaSuggestions(suggestions);
      })
      .catch(() => {
        if (active) setServiceAreaSuggestions([]);
      })
      .finally(() => {
        if (active) setServiceAreaSuggestionsLoading(false);
      });

    return () => {
      active = false;
    };
  }, [
    coverageAreaRadiusKm,
    coverageAreaSearchQuery,
    formData,
    formData?.id,
    isOpen,
  ]);

  if (!isOpen) return null;

  // Se formData ainda não foi inicializado, não renderiza nada
  if (!formData) return null;

  // Usar o handleChange do hook ou o local
  const handleChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
    >
  ) => {
    handleFormChange(e);
  };

  const handlePrimaryCategoryChange = (
    e: React.ChangeEvent<HTMLSelectElement>
  ) => {
    const selectedId = e.target.value;
    const selected = categories.find((c) => c.gcid === selectedId);
    setCategoriesTouched(true);
    setCategoryFeedback("");
    setFormData((prev) =>
      prev
        ? {
            ...prev,
            categoryId: selectedId || undefined,
            category: selected?.category || "",
            secondaryCategoryIds: (prev.secondaryCategoryIds || []).filter(
              (id) => id !== selectedId,
            ),
            secondaryCategories: (prev.secondaryCategories || []).filter(
              (name) => name !== selected?.category,
            ),
          }
        : null
    );
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData) return;
    if (profile && hasNewRiskyPublishedValue(profile, formData) && !window.confirm(SAFE_PUBLISH_WARNING)) {
      return;
    }

    setSaveFeedback(null);
    setLoading(true);
    try {
      await onSave(
        categoriesTouched || !profile
          ? formData
          : {
              ...formData,
              category: profile.category,
              categoryId: profile.categoryId,
              secondaryCategories: profile.secondaryCategories,
              secondaryCategoryIds: profile.secondaryCategoryIds,
            },
      );
      onClose();
    } catch (error) {
      setSaveFeedback(
        getErrorMessage(error, "Nao foi possivel salvar as alteracoes do perfil."),
      );
    } finally {
      setLoading(false);
    }
  };

  const handleOverlayClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const getErrorMessage = (error: unknown, fallback: string) =>
    error instanceof Error ? error.message : fallback;

  const getCurrentGabarito = () =>
    formData ? readGabaritoState(formData.id) : null;

  /** Marca/desmarca uma categoria secundária respeitando as travas (principal repetida, máximo 8). */
  const alternarCategoriaSecundaria = (cat: { gcid: string; category: string }, marcar: boolean) => {
    if (!formData) return;
    const currentNames = formData.secondaryCategories || [];
    const currentIds = formData.secondaryCategoryIds || [];
    if (marcar && (cat.gcid === primaryCategoryValue || cat.category === formData.category)) {
      setCategoryFeedback("A categoria principal nao pode ser repetida como secundaria.");
      return;
    }
    if (marcar && currentIds.length >= 8 && !currentIds.includes(cat.gcid)) {
      setCategoryFeedback("Limite atingido: escolha no maximo 8 categorias secundarias.");
      return;
    }
    setCategoriesTouched(true);
    setCategoryFeedback("");
    const updatedNames = marcar ? Array.from(new Set([...currentNames, cat.category])) : currentNames.filter((c) => c !== cat.category);
    const updatedIds = marcar ? Array.from(new Set([...currentIds, cat.gcid])) : currentIds.filter((id) => id !== cat.gcid);
    setFormData((prev) => (prev ? { ...prev, secondaryCategories: updatedNames, secondaryCategoryIds: updatedIds } : null));
  };

  const removerCategoriaSecundaria = (nome: string) => {
    setCategoriesTouched(true);
    setCategoryFeedback("");
    const categoryId = categories.find((category) => category.category === nome)?.gcid;
    setFormData((prev) =>
      prev
        ? {
            ...prev,
            secondaryCategories: (prev.secondaryCategories || []).filter((c) => c !== nome),
            secondaryCategoryIds: prev.secondaryCategoryIds?.filter((id) => id !== categoryId),
          }
        : null,
    );
  };

  const applySuggestedCategory = (category: CategorySuggestion) => {
    setCategoriesTouched(true);
    setCategoryFeedback("");
    setFormData((prev) =>
      prev ? applyCategorySuggestion(prev, category) : null,
    );
  };

  const addServiceArea = (area: string) => {
    const nextArea = area.trim();
    if (!nextArea) return;

    setFormData((prev) =>
      prev
        ? {
            ...prev,
            serviceAreas: Array.from(
              new Map(
                [...(prev.serviceAreas || []), nextArea].map((serviceArea) => [
                  serviceArea.toLowerCase().trim(),
                  serviceArea,
                ]),
              ).values(),
            ),
          }
        : null,
    );
    setCoverageAreaSearchQuery("");
  };

  const removeServiceArea = (area: string) => {
    setFormData((prev) =>
      prev
        ? {
            ...prev,
            serviceAreas: (prev.serviceAreas || []).filter(
              (serviceArea) =>
                serviceArea.toLowerCase().trim() !== area.toLowerCase().trim(),
            ),
          }
        : null,
    );
  };

  const addTypedServiceArea = () => {
    addServiceArea(coverageAreaSearchQuery);
  };

  const fillSuggestedServiceAreas = () => {
    setFormData((prev) =>
      prev
        ? {
            ...prev,
            serviceAreas: Array.from(
              new Map(
                [
                  ...(prev.serviceAreas || []),
                  ...serviceAreaSuggestions.map((suggestion) => suggestion.label),
                ].map((serviceArea) => [
                  serviceArea.toLowerCase().trim(),
                  serviceArea,
                ]),
              ).values(),
            ),
          }
        : null,
    );
    if (formData?.id) {
      setAutoFilledAreaProfileId(formData.id);
    }
  };

  const handleSuggestCategoriesWithContext = async () => {
    setSaveFeedback(null);
    if (!formData || !iaDescription.trim()) {
      setIaFeedback({
        type: "info",
        message: "Descreva o negocio ou objetivo SEO para receber sugestoes.",
      });
      return;
    }

    setIaLoadingAction("categories");
    setIaFeedback(null);
    try {
      const suggestions = await profileAdvisorService.getSuggestedCategories({
        displayName: formData.name,
        formattedAddress: buildFormattedAddress(formData),
        types: [iaDescription],
      });
      const officialSuggestions = filterOfficialCategorySuggestions({
        suggestions,
        officialCategories: categories,
        context: [
          formData.name,
          formData.category,
          buildFormattedAddress(formData),
          iaDescription,
        ].join(" "),
      });

      setSuggestedCategories(officialSuggestions);
      if (officialSuggestions.length > 0) {
        setActiveTab("basico");
      }
      setIaFeedback({
        type: officialSuggestions.length > 0 ? "success" : "info",
        message:
          officialSuggestions.length > 0
            ? "Categorias oficiais sugeridas na aba Informacoes Basicas."
            : "Nenhuma categoria oficial foi compatível com este contexto.",
      });
    } catch (error) {
      setIaFeedback({
        type: "error",
        message: getErrorMessage(error, "Erro ao sugerir categorias."),
      });
    } finally {
      setIaLoadingAction(null);
    }
  };

  const handleGenerateCommercialPlan = async () => {
    setSaveFeedback(null);
    if (!formData || !iaDescription.trim()) {
      setIaFeedback({
        type: "info",
        message:
          "Descreva o objetivo comercial para gerar o plano de SEO.",
      });
      return;
    }
    setIaLoadingAction("commercial");
    setIaFeedback(null);
    try {
      const plan = await seoProfileApi.research(formData.id, iaDescription);
      setCommercialPlan(plan);
      const total = plan.itens.length + plan.produtos.length;
      setIaFeedback({
        type: total > 0 ? "success" : "info",
        message:
          total > 0
            ? "Plano comercial criado. Abra a aba SEO para revisar e publicar."
            : "A IA nao encontrou oportunidades comerciais suficientes para este contexto.",
      });
    } catch (error) {
      setIaFeedback({
        type: "error",
        message: getErrorMessage(error, "Erro ao gerar plano comercial."),
      });
    } finally {
      setIaLoadingAction(null);
    }
  };

  const handleSuggestKeywordsWithContext = async () => {
    setSaveFeedback(null);
    if (!formData || !iaDescription.trim()) {
      setIaFeedback({
        type: "info",
        message: "Descreva o objetivo SEO para receber palavras-chave.",
      });
      return;
    }

    setIaLoadingAction("keywords");
    setIaFeedback(null);
    try {
      const gabarito = getCurrentGabarito();
      const keywords = await profileAdvisorService.getSuggestedKeywords({
        displayName: formData.name,
        formattedAddress: buildFormattedAddress(formData),
        categories: buildAdvisorCategories(formData, iaDescription),
        currentKeywords: [
          ...(gabarito?.postKeywords || "").split(","),
          ...(gabarito?.commentKeywords || "").split(","),
        ]
          .map((k) => k.trim())
          .filter(Boolean),
      });
      const contextualKeywords = buildAdvisorKeywords({
        suggestedKeywords: keywords,
        iaDescription,
        gabaritoPostKeywords: gabarito?.postKeywords || "",
        gabaritoCommentKeywords: gabarito?.commentKeywords || "",
      });

      setKeywordDrafts(createKeywordDrafts(contextualKeywords, formData));
      setIaFeedback({
        type: contextualKeywords.length > 0 ? "success" : "info",
        message:
          contextualKeywords.length > 0
            ? "Revise as palavras-chave antes de aplicar no Gabarito."
            : "A IA nao retornou palavras-chave para este contexto.",
      });
    } catch (error) {
      setIaFeedback({
        type: "error",
        message: getErrorMessage(error, "Erro ao sugerir palavras-chave."),
      });
    } finally {
      setIaLoadingAction(null);
    }
  };

  const handleGenerateBriefingWithContext = async () => {
    setSaveFeedback(null);
    if (!formData || !iaDescription.trim()) {
      setIaFeedback({
        type: "info",
        message: "Descreva o negocio ou objetivo SEO para gerar a descricao.",
      });
      return;
    }

    setIaLoadingAction("briefing");
    setIaFeedback(null);
    try {
      const gabarito = getCurrentGabarito();
      const categoriesArray = buildAdvisorCategories(
        formData,
        iaDescription,
        suggestedCategories.map((category) => category.category),
      );
      const keywordsArray = buildAdvisorKeywords({
        suggestedKeywords: sanitizeKeywordSuggestions(
          selectedKeywordValues,
          formData,
        ),
        iaDescription,
        gabaritoPostKeywords: gabarito?.postKeywords || "",
        gabaritoCommentKeywords: gabarito?.commentKeywords || "",
      });

      // A descrição vem da IA; se ela falhar (sem crédito, backend antigo), a
      // tela cai no texto montado aqui e avisa, em vez de ficar sem nada.
      let publicDescription = "";
      let fromAI = true;
      try {
        publicDescription = await profileAdvisorService.getSuggestedDescription(
          {
            displayName: formData.name,
            formattedAddress: buildFormattedAddress(formData),
            primaryCategory: formData.category || undefined,
            categories: categoriesArray,
            keywords: keywordsArray.length > 0 ? keywordsArray : undefined,
            context: iaDescription.trim(),
          },
        );
      } catch {
        publicDescription = "";
      }
      if (!publicDescription.trim()) {
        fromAI = false;
        publicDescription = buildPublicBusinessDescription({
          profile: formData,
          keywords: keywordsArray,
        });
      }
      setFormData((prev) =>
        prev ? { ...prev, description: publicDescription } : null,
      );
      setActiveTab("descricao");
      setIaFeedback(
        fromAI
          ? {
              type: "success",
              message:
                "Descricao gerada pela IA e preenchida na aba Descricao. Revise antes de salvar.",
            }
          : {
              type: "info",
              message:
                "A IA nao respondeu agora. Preenchi um texto-base na aba Descricao: revise antes de salvar.",
            },
      );
    } catch (error) {
      setIaFeedback({
        type: "error",
        message: `Não foi possível gerar descrição pela IA: ${getErrorMessage(
          error,
          "motivo não identificado.",
        )}`,
      });
    } finally {
      setIaLoadingAction(null);
    }
  };

  const handleApplyKeywordsToGabarito = async () => {
    if (!formData || selectedKeywordValues.length === 0) return;
    const cleanKeywords = sanitizeKeywordSuggestions(
      selectedKeywordValues,
      formData,
    );
    if (cleanKeywords.length === 0) {
      setIaFeedback({
        type: "info",
        message: "Revise as palavras-chave antes de aplicar no Gabarito.",
      });
      return;
    }
    const gabarito = readGabaritoState(formData.id);
    const updatedGabarito = buildCompleteGabarito({
      profile: formData,
      existing: {
        ...gabarito,
        postKeywords: appendKeywords(gabarito.postKeywords, cleanKeywords),
      },
      keywords: cleanKeywords,
    });

    writeGabaritoState(formData.id, updatedGabarito);
    try {
      await profilesTrackingService.saveGabaritoText(formData.id, {
        postKeywords: updatedGabarito.postKeywords ?? "",
        commentKeywords: updatedGabarito.commentKeywords ?? "",
        postNotes: updatedGabarito.postNotes ?? "",
        commentNotes: updatedGabarito.commentNotes ?? "",
      });
      setIaFeedback({
        type: "success",
        message: "Palavras-chave revisadas aplicadas no Gabarito deste perfil.",
      });
    } catch (error: unknown) {
      setIaFeedback({
        type: "error",
        message: `Erro ao salvar Gabarito: ${getErrorMessage(
          error,
          "motivo nao identificado.",
        )}`,
      });
    }
  };

  const DIAS_UTEIS = ["tuesday", "wednesday", "thursday", "friday"] as const;
  const copiarSegundaParaDiasUteis = () =>
    setFormData((prev) => {
      if (!prev) return null;
      const segunda = prev.hours?.monday ?? "Fechado";
      const hours = { ...prev.hours };
      for (const dia of DIAS_UTEIS) hours[dia] = Array.isArray(segunda) ? [...segunda] : segunda;
      return { ...prev, hours };
    });
  const aplicarHorarioComercial = () =>
    setFormData((prev) => {
      if (!prev) return null;
      const hours = { ...prev.hours };
      for (const dia of ["monday", ...DIAS_UTEIS] as const) hours[dia] = "08:00 - 18:00";
      return { ...prev, hours };
    });

  // Seções do editor, agrupadas como no redesign V3. Mesmos ids e conteúdos de antes.
  const tabs = [
    { id: "basico", label: "Informações básicas", icon: "business", group: "Empresa" },
    { id: "contato", label: "Contato", icon: "contact_phone", group: "Empresa" },
    { id: "local", label: "Local e áreas", icon: "place", group: "Empresa" },
    { id: "horarios", label: "Horários", icon: "schedule", group: "Empresa" },
    { id: "descricao", label: "Descrição", icon: "description", group: "Empresa" },
    { id: "servicos", label: "Serviços", icon: "build", group: "Conteúdo" },
    { id: "produtos", label: "Produtos", icon: "inventory_2", group: "Conteúdo" },
    { id: "postagens", label: "Postagens", icon: "feed", group: "Conteúdo" },
    { id: "gabarito", label: "Gabarito", icon: "assignment", group: "Conteúdo" },
    { id: "avaliacoes", label: "Avaliações", icon: "reviews", group: "Conteúdo" },
    { id: "atributos", label: "Mais atributos", icon: "tune", group: "Otimização" },
    { id: "ia", label: "Assistente IA", icon: "auto_awesome", group: "Otimização" },
    { id: "seo", label: "SEO", icon: "travel_explore", group: "Otimização" },
    { id: "presenca", label: "Presença digital", icon: "qr_code_2", group: "Distribuição" },
    // Piloto: so a equipe ve a aba enquanto a Meta nao aprova o app para clientes.
    ...(INSTAGRAM_TAB_ENABLED && isInternalUser
      ? [{ id: "instagram", label: "Instagram", icon: "photo_camera", group: "Distribuição" }]
      : []),
  ]
    .filter((tab) => !(hideIATab && tab.id === "ia"))
    // O cliente já tem a página Avaliações no menu; a aba é o acesso da equipe.
    .filter((tab) => !(tab.id === "avaliacoes" && !isInternalUser));
  const tabGroups = tabs.reduce<{ title: string; items: typeof tabs }[]>((groups, tab) => {
    const group = groups.find((g) => g.title === tab.group);
    if (group) group.items.push(tab);
    else groups.push({ title: tab.group, items: [tab] });
    return groups;
  }, []);
  const localidade = [formData.city, formData.state].filter(Boolean).join(", ");
  // Quantos campos diferem do perfil carregado (selo "N alterações não salvas" do rodapé).
  const alteracoesNaoSalvas = profile
    ? (Object.keys(formData) as (keyof Profile)[]).filter(
        (chave) => JSON.stringify(formData[chave] ?? null) !== JSON.stringify(profile[chave] ?? null),
      ).length
    : 0;
  const corDoStatus =
    formData.status === "active" ? "#1E8E3E"
    : formData.status === "paused" ? "#F9AB00"
    : formData.status === "pending" ? "#1264C7"
    : "#9AA0A6";
  const semPrefixo = (valor: string | undefined, prefixo: RegExp) => !valor || !prefixo.test(valor);
  const alternarAtributo = (chave: ChaveDeAtributo, item: string, marcar: boolean) => {
    setFormData((prev) => {
      if (!prev) return null;
      const atual = prev[chave] || [];
      const novo = marcar ? Array.from(new Set([...atual, item])) : atual.filter((i) => i !== item);
      return { ...prev, [chave]: novo };
    });
  };
  const atributosPreenchidos = GRUPOS_DE_ATRIBUTOS.reduce((soma, grupo) => soma + (formData[grupo.chave] || []).filter((i) => grupo.itens.includes(i)).length, 0);
  const filtroDeAtributo = buscaAtributo.trim().toLowerCase();
  const gabaritoAtual = readGabaritoState(formData.id);
  const palavrasDoGabarito = Array.from(
    new Set(
      [...(gabaritoAtual?.postKeywords || "").split(","), ...(gabaritoAtual?.commentKeywords || "").split(",")]
        .map((palavra) => palavra.trim())
        .filter(Boolean),
    ),
  );

  return (
    <ModalOverlay onClick={handleOverlayClick}>
      <ModalContainer>
        <ModalHeader>
          <HeaderContent>
            <HeaderIcon>
              <span className="material-icons">storefront</span>
            </HeaderIcon>
            <HeaderText>
              <ModalTitle>{formData.name || "Gerenciar Perfil"}</ModalTitle>
              <ModalSubtitle>
                Gerenciar perfil{localidade ? ` · ${localidade}` : ""}
                {formData.category ? ` · ${formData.category}` : ""}
              </ModalSubtitle>
            </HeaderText>
          </HeaderContent>
          <CloseButton type="button" onClick={onClose} aria-label="Fechar editor" title="Fechar">
            <span className="material-icons">close</span>
          </CloseButton>
        </ModalHeader>

        <EditorLayout>
        <TabsContainer aria-label="Seções do perfil">
          {tabGroups.map((group) => (
            <div key={group.title} style={{ display: "contents" }}>
              <TabGroupTitle>{group.title}</TabGroupTitle>
              {group.items.map((tab) => (
                <Tab
                  key={tab.id}
                  type="button"
                  $active={activeTab === tab.id}
                  aria-current={activeTab === tab.id ? "page" : undefined}
                  title={tab.label}
                  onClick={() => {
                    if (tab.id !== "local") {
                      setShowMapPicker(false);
                    }
                    setActiveTab(tab.id);
                    setSaveFeedback(null);
                  }}
                >
                  <span className="material-icons">{tab.icon}</span>
                  {tab.label}
                </Tab>
              ))}
            </div>
          ))}
          {loggedUser?.userType === "ADMIN" && formData.id && (
            <EncerrarImpulsionamento profileId={formData.id} nomeDaEmpresa={formData.name || ""} />
          )}
        </TabsContainer>

        <form
          onSubmit={handleSubmit}
          style={{
            display: "flex",
            flexDirection: "column",
            flex: "1 1 auto",
            minHeight: 0,
          }}
        >
          <ModalBody>
            {activeTab === "basico" && (
              <EditorGrid>
                <Coluna>
                  <FormSection style={{ maxWidth: "none" }}>
                    <CabecalhoDaSecao
                      titulo={<>Informações básicas do negócio<FieldSyncBadge kind="backend" /></>}
                      apoio="Nome, categoria e status são os campos que mais pesam para o Google entender o que você faz."
                    />

                    <FormGroup>
                      <Label htmlFor="name">
                        <span className="material-icons">store</span>
                        Nome da empresa
                        <FieldSyncBadge kind="backend" />
                      </Label>
                      <Input
                        id="name"
                        name="name"
                        type="text"
                        value={formData.name}
                        onChange={handleChange}
                        placeholder="Ex: Restaurante Sabor da Terra"
                        required
                      />
                      <Hint>Use o nome real da fachada. Palavras-chave no nome violam as diretrizes do Google.</Hint>
                    </FormGroup>

                    <FormGrid>
                      <FormGroup>
                        <Label htmlFor="category">
                          <span className="material-icons">category</span>
                          Categoria principal
                          <FieldSyncBadge kind="backend" />
                        </Label>
                        <Select
                          id="category"
                          name="category"
                          value={primaryCategoryValue}
                          onChange={handlePrimaryCategoryChange}
                        >
                          <option value="">Selecione uma categoria</option>
                          {primaryCategoryOptions}
                        </Select>
                        <Campo>
                          <span className="material-icons">search</span>
                          <input
                            type="text"
                            placeholder="Pesquisar categoria principal"
                            aria-label="Pesquisar categoria principal"
                            value={primaryCategoriesSearchQuery}
                            onChange={(e) => setPrimaryCategoriesSearchQuery(e.target.value)}
                          />
                        </Campo>
                        <Hint>
                          Atual no Google: <b style={{ color: "#202124", fontWeight: 500 }}>{formData.category || "não informada"}</b>
                          {formData.categoryId ? ` · ${formData.categoryId}` : ""}
                        </Hint>
                      </FormGroup>

                      <FormGroup>
                        <Label htmlFor="status">
                          <span className="material-icons">toggle_on</span>
                          Status do negócio
                          <FieldSyncBadge kind="backend" />
                        </Label>
                        <Campo>
                          <Ponto $cor={corDoStatus} />
                          <select id="status" name="status" value={formData.status} onChange={handleChange}>
                            <option value="active">Aberto e operacional</option>
                            <option value="paused">Fechado temporariamente</option>
                            <option value="pending">Abrirá em breve</option>
                            <option value="preparing">Em preparação</option>
                          </select>
                          <span className="material-icons">expand_more</span>
                        </Campo>
                        <Hint>Aparece na ficha como "Aberto" ou "Fechado temporariamente".</Hint>
                      </FormGroup>
                    </FormGrid>

                    {suggestedCategories.length > 0 && (
                      <FormGroup>
                        <Label>
                          <span className="material-icons">auto_awesome</span>
                          Sugestões oficiais de categoria
                        </Label>
                        <Hint>Escolha somente categorias da lista oficial do Google para evitar sugestões fora do segmento.</Hint>
                        <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                          {suggestedCategories.map((category) => {
                            // Só vira principal se o perfil ainda não tem uma.
                            const viraPrincipal = category.primary && !formData.categoryId;
                            return (
                              <ChipSugestao key={category.gcid} type="button" onClick={() => applySuggestedCategory(category)}>
                                <span className="material-icons">add</span>
                                {viraPrincipal ? "Principal: " : ""}
                                {category.category}
                              </ChipSugestao>
                            );
                          })}
                        </div>
                      </FormGroup>
                    )}

                    <FormGrid>
                      <FormGroup>
                        <Label htmlFor="openingDate">
                          <span className="material-icons">event</span>
                          Data de abertura
                          <FieldSyncBadge kind="backend" />
                        </Label>
                        <Campo>
                          <span className="material-icons">calendar_today</span>
                          <input
                            id="openingDate"
                            name="openingDate"
                            type="date"
                            value={formData.openingDate || ""}
                            onChange={handleChange}
                          />
                        </Campo>
                        <Hint>Opcional. O Google mostra "Aberto há X anos" na ficha.</Hint>
                      </FormGroup>
                    </FormGrid>

                    <FormGroup style={{ marginBottom: 0 }}>
                      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12 }}>
                        <Label>
                          <span className="material-icons">list</span>
                          Categorias adicionais
                          <FieldSyncBadge kind="backend" />
                        </Label>
                        <Badge>{`${(formData.secondaryCategories || []).length} de 8 usadas`}</Badge>
                      </div>
                      <ChipBox>
                        {(formData.secondaryCategories || []).map((cat) => (
                          <Chip key={cat}>
                            <span className="texto">{cat}</span>
                            <ChipX type="button" aria-label={`Remover categoria ${cat}`} onClick={() => removerCategoriaSecundaria(cat)}>
                              <span className="material-icons">close</span>
                            </ChipX>
                          </Chip>
                        ))}
                        <ChipBusca>
                          <span className="material-icons">add</span>
                          <input
                            type="text"
                            placeholder="Pesquisar categoria para adicionar"
                            aria-label="Pesquisar categoria para adicionar"
                            value={categoriesSearchQuery}
                            onChange={(e) => setCategoriesSearchQuery(e.target.value)}
                          />
                        </ChipBusca>
                      </ChipBox>
                      {categoryFeedback && (
                        <Hint style={{ color: "#B06000", fontWeight: 500 }}>{categoryFeedback}</Hint>
                      )}
                      {loadingCategories ? (
                        <Hint>Carregando categorias...</Hint>
                      ) : (
                        categoriesSearchQuery.trim() !== "" && (
                          <div
                            role="listbox"
                            aria-label="Categorias encontradas"
                            style={{ maxHeight: 280, overflowY: "auto", border: "1px solid #DADCE0", borderRadius: 12, padding: 8, background: "#fff", display: "flex", flexDirection: "column", gap: 4 }}
                          >
                            {visibleCategories.map((cat) => {
                              const marcada =
                                formData.secondaryCategoryIds?.includes(cat.gcid) ||
                                formData.secondaryCategories?.includes(cat.category) ||
                                false;
                              const travada =
                                cat.gcid === primaryCategoryValue ||
                                cat.category === formData.category ||
                                ((formData.secondaryCategoryIds || []).length >= 8 && !marcada);
                              return (
                                <label
                                  key={cat.gcid}
                                  style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 12px", borderRadius: 8, cursor: travada ? "default" : "pointer", opacity: travada ? 0.6 : 1 }}
                                >
                                  <input
                                    type="checkbox"
                                    checked={marcada}
                                    disabled={travada}
                                    onChange={(e) => alternarCategoriaSecundaria(cat, e.target.checked)}
                                    style={{ width: 18, height: 18, accentColor: "#1264C7" }}
                                  />
                                  <span style={{ fontSize: 14, color: "#202124", flex: 1 }}>{cat.category}</span>
                                </label>
                              );
                            })}
                            {visibleCategories.length === 0 && <Hint style={{ padding: 8 }}>Nenhuma categoria encontrada</Hint>}
                          </div>
                        )
                      )}
                      <Tip $tom="aviso">
                        <span className="material-icons">warning</span>
                        Ao remover uma categoria, o Google pode excluir os serviços ligados a ela e não permitir recriá-los automaticamente. A categoria principal não pode se repetir aqui.
                      </Tip>
                    </FormGroup>
                  </FormSection>
                </Coluna>
                <Aside>
                  <PreviaNoGoogle perfil={formData} />
                  <Completude perfil={formData} />
                  <Tip>
                    <span className="material-icons">lightbulb</span>
                    Categorias coerentes ajudam o Google a entender com mais precisão o que a empresa faz.
                  </Tip>
                </Aside>
              </EditorGrid>
            )}

            {activeTab === "contato" && (
              <EditorGrid>
                <Coluna>
                  <FormSection style={{ maxWidth: "none" }}>
                    <CabecalhoDaSecao
                      titulo={<>Informações de contato<FieldSyncBadge kind="backend" /></>}
                      apoio={<>Como o cliente fala com você a partir da ficha. Telefone e WhatsApp viram botões "Ligar" e "Conversar" no Google.</>}
                    />

                    <FormGrid>
                      <FormGroup>
                        <Label htmlFor="phone">
                          <span className="material-icons">call</span>
                          Telefone principal
                          <FieldSyncBadge kind="backend" />
                        </Label>
                        <Campo>
                          {semPrefixo(formData.phone, /^\+/) && <Prefixo>+55</Prefixo>}
                          <input
                            id="phone"
                            name="phone"
                            type="tel"
                            value={formData.phone || ""}
                            onChange={handleChange}
                            placeholder="(11) 98765-4321"
                          />
                          {formData.phone && <span className="material-icons" style={{ color: "#137333", fontSize: 16 }}>check_circle</span>}
                        </Campo>
                      </FormGroup>

                      <FormGroup>
                        <Label htmlFor="email">
                          <span className="material-icons">mail</span>
                          E-mail de contato
                          <FieldSyncBadge kind="backend" />
                        </Label>
                        <Input
                          id="email"
                          name="email"
                          type="email"
                          value={formData.email || ""}
                          onChange={handleChange}
                          placeholder="contato@suaempresa.com.br"
                        />
                        <Hint>Não aparece na ficha pública. Usado para avisos e recuperação de acesso.</Hint>
                      </FormGroup>
                    </FormGrid>

                    <FormGroup>
                      <Label htmlFor="website">
                        <span className="material-icons">language</span>
                        Website / página oficial
                        <FieldSyncBadge kind="backend" />
                      </Label>
                      <Campo>
                        {semPrefixo(formData.website, /^https?:\/\//i) && <Prefixo>https://</Prefixo>}
                        <input
                          id="website"
                          name="website"
                          type="url"
                          value={formData.website || ""}
                          onChange={handleChange}
                          placeholder="www.suaempresa.com.br"
                        />
                        {formData.website && (
                          <a
                            href={/^https?:\/\//i.test(formData.website) ? formData.website : `https://${formData.website}`}
                            target="_blank"
                            rel="noreferrer"
                            className="material-icons"
                            aria-label="Abrir site"
                            style={{ fontSize: 18, color: "#1264C7", textDecoration: "none" }}
                          >
                            open_in_new
                          </a>
                        )}
                      </Campo>
                    </FormGroup>

                    <FormGroup style={{ marginBottom: 0 }}>
                      <Label htmlFor="whatsapp">
                        <span className="material-icons">chat</span>
                        WhatsApp (chat)
                        <FieldSyncBadge kind="backend" />
                      </Label>
                      <Campo>
                        <input
                          id="whatsapp"
                          name="whatsapp"
                          type="text"
                          value={formData.whatsapp || ""}
                          onChange={handleChange}
                          placeholder="https://wa.me/5511999999999"
                        />
                        {formData.whatsapp && /^https?:\/\//i.test(formData.whatsapp) && (
                          <a href={formData.whatsapp} target="_blank" rel="noreferrer" className="material-icons" aria-label="Abrir conversa" style={{ fontSize: 18, color: "#1264C7", textDecoration: "none" }}>
                            open_in_new
                          </a>
                        )}
                      </Campo>
                      <Hint>Guardado só no GeoRanking. Para o botão aparecer no Google, use o bloco "WhatsApp no Google" abaixo.</Hint>
                    </FormGroup>
                  </FormSection>

                  {formData.id && <WhatsappNoGoogle profileId={formData.id} />}

                  <FormSection style={{ maxWidth: "none" }}>
                    <CabecalhoDaSecao
                      titulo="Perfis de redes sociais"
                      apoio="Aparecem como ícones na ficha. Cole o link completo do perfil."
                      extra={
                        <Badge $tom="verde">
                          {`${[formData.socialMedia?.facebook, formData.socialMedia?.instagram, formData.socialMedia?.linkedin].filter(Boolean).length} conectadas`}
                        </Badge>
                      }
                    />
                    <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 20 }}>
                      <FormGroup style={{ marginBottom: 0 }}>
                        <Label htmlFor="socialMedia.facebook">
                          <span className="material-icons">thumb_up</span>
                          Facebook
                          <FieldSyncBadge kind="backend" />
                        </Label>
                        <Campo>
                          {semPrefixo(formData.socialMedia?.facebook, /^https?:\/\//i) && <Prefixo>facebook.com/</Prefixo>}
                          <input
                            id="socialMedia.facebook"
                            name="socialMedia.facebook"
                            type="url"
                            value={formData.socialMedia?.facebook || ""}
                            onChange={handleChange}
                            placeholder="https://www.facebook.com/suaempresa"
                          />
                        </Campo>
                      </FormGroup>
                      <FormGroup style={{ marginBottom: 0 }}>
                        <Label htmlFor="socialMedia.instagram">
                          <span className="material-icons">photo_camera</span>
                          Instagram
                          <FieldSyncBadge kind="backend" />
                        </Label>
                        <Campo>
                          {semPrefixo(formData.socialMedia?.instagram, /^https?:\/\//i) && <Prefixo>instagram.com/</Prefixo>}
                          <input
                            id="socialMedia.instagram"
                            name="socialMedia.instagram"
                            type="url"
                            value={formData.socialMedia?.instagram || ""}
                            onChange={handleChange}
                            placeholder="https://www.instagram.com/suaempresa"
                          />
                        </Campo>
                      </FormGroup>
                      <FormGroup style={{ marginBottom: 0 }}>
                        <Label htmlFor="socialMedia.linkedin">
                          <span className="material-icons">work</span>
                          LinkedIn
                          <FieldSyncBadge kind="backend" />
                        </Label>
                        <Campo>
                          {semPrefixo(formData.socialMedia?.linkedin, /^https?:\/\//i) && <Prefixo>linkedin.com/company/</Prefixo>}
                          <input
                            id="socialMedia.linkedin"
                            name="socialMedia.linkedin"
                            type="url"
                            value={formData.socialMedia?.linkedin || ""}
                            onChange={handleChange}
                            placeholder="https://www.linkedin.com/company/suaempresa"
                          />
                        </Campo>
                      </FormGroup>
                    </div>
                  </FormSection>
                </Coluna>
                <Aside>
                  {isInternalUser && <ConfirmacaoDoWhatsappDoCliente profileId={formData.id} />}
                  <AsideCard style={{ gap: 14 }}>
                    <Lab>Botões na ficha do Google</Lab>
                    <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                      {[
                        { icone: "call", rotulo: "Ligar", ativo: Boolean(formData.phone) },
                        { icone: "chat", rotulo: "Conversar", ativo: Boolean(formData.whatsapp) },
                        { icone: "language", rotulo: "Site", ativo: Boolean(formData.website) },
                        { icone: "directions", rotulo: "Rotas", ativo: Boolean(formData.address) },
                      ].map((botao) => (
                        <BotaoPequeno key={botao.rotulo} type="button" $variante="outlined" style={{ pointerEvents: "none", opacity: botao.ativo ? 1 : 0.45 }} aria-disabled>
                          <span className="material-icons">{botao.icone}</span>
                          {botao.rotulo}
                        </BotaoPequeno>
                      ))}
                    </div>
                    <Hint>
                      {[formData.phone, formData.whatsapp, formData.website, formData.address].filter(Boolean).length === 4
                        ? "Todos os quatro botões ativos. Ligações e conversas contam no relatório de desempenho."
                        : "Preencha telefone, WhatsApp, site e endereço para ativar os quatro botões da ficha."}
                    </Hint>
                  </AsideCard>
                  <Tip>
                    <span className="material-icons">lightbulb</span>
                    O WhatsApp adiciona uma opção direta de contato quando está disponível na ficha.
                  </Tip>
                </Aside>
              </EditorGrid>
            )}

            {activeTab === "local" && (
              <EditorGrid>
                <Coluna>
                  <FormSection style={{ maxWidth: "none" }}>
                    <CabecalhoDaSecao
                      titulo={<>Local da empresa<FieldSyncBadge kind="backend" /></>}
                      apoio="Endereço da fachada. Alterar o endereço atualiza a ficha no Google e pode exigir reverificação por alguns dias."
                      extra={
                        <Badge $tom="amarelo" role="status">
                          <span className="material-icons">lock</span>
                          Edite só se necessário
                        </Badge>
                      }
                    />

                    <div className="grade-celular" style={{ display: "grid", gridTemplateColumns: "minmax(0, 2fr) minmax(0, 1fr)", gap: 20, marginBottom: 22 }}>
                      <FormGroup style={{ marginBottom: 0 }}>
                        <Label htmlFor="address">
                          <span className="material-icons">home</span>
                          Endereço completo
                          <FieldSyncBadge kind="backend" />
                        </Label>
                        <Input id="address" name="address" type="text" value={formData.address || ""} onChange={handleChange} placeholder="Rua, Avenida, número, complemento" />
                      </FormGroup>
                      <FormGroup style={{ marginBottom: 0 }}>
                        <Label htmlFor="zipCode">
                          <span className="material-icons">markunread_mailbox</span>
                          CEP
                          <FieldSyncBadge kind="backend" />
                        </Label>
                        <Input id="zipCode" name="zipCode" type="text" value={formData.zipCode || ""} onChange={handleChange} placeholder="00000-000" />
                      </FormGroup>
                    </div>

                    <div className="grade-celular" style={{ display: "grid", gridTemplateColumns: "repeat(3, minmax(0, 1fr))", gap: 20, marginBottom: 22 }}>
                      <FormGroup style={{ marginBottom: 0 }}>
                        <Label htmlFor="city">
                          <span className="material-icons">location_city</span>
                          Cidade
                          <FieldSyncBadge kind="backend" />
                        </Label>
                        <Input id="city" name="city" type="text" value={formData.city || ""} onChange={handleChange} placeholder="Ex: São Paulo" />
                      </FormGroup>
                      <FormGroup style={{ marginBottom: 0 }}>
                        <Label htmlFor="state">
                          <span className="material-icons">map</span>
                          Estado (UF)
                          <FieldSyncBadge kind="backend" />
                        </Label>
                        <Input id="state" name="state" type="text" value={formData.state || ""} onChange={handleChange} placeholder="Ex: SP" maxLength={2} />
                      </FormGroup>
                      <FormGroup style={{ marginBottom: 0 }}>
                        <Label htmlFor="country">
                          <span className="material-icons">public</span>
                          País
                          <FieldSyncBadge kind="backend" />
                        </Label>
                        <Input id="country" name="country" type="text" value={formData.country || ""} onChange={handleChange} placeholder="Brasil" />
                      </FormGroup>
                    </div>

                    <FormGroup style={{ marginBottom: 0 }}>
                      <Label>
                        <span className="material-icons">location_on</span>
                        Localização no mapa
                        <FieldSyncBadge kind="backend" />
                      </Label>
                      {showMapPicker ? (
                        <LocationPicker
                          latitude={formData.latitude ?? serviceAreaOrigin?.lat}
                          longitude={formData.longitude ?? serviceAreaOrigin?.lng}
                          onLocationChange={(lat, lng) => {
                            setFormData((prev) => (prev ? { ...prev, latitude: lat, longitude: lng } : null));
                          }}
                          onRadiusChange={setCoverageAreaRadiusKm}
                          radiusKm={coverageAreaRadiusKm}
                          startAddress={[
                            formData.address,
                            [formData.city, formData.state].filter(Boolean).join(" - "),
                          ]
                            .filter(Boolean)
                            .join(", ")}
                        />
                      ) : (
                        <div className="grade-celular" style={{ display: "grid", gridTemplateColumns: "minmax(0, 1fr) 260px", gap: 16, alignItems: "stretch" }}>
                          <div style={{ height: 168, borderRadius: 14, background: "linear-gradient(135deg, #EAF1FB, #F1F3F4 60%, #E6F4EA)", border: "1px solid #DADCE0", position: "relative", overflow: "hidden" }}>
                            <div style={{ position: "absolute", inset: 0, backgroundImage: "linear-gradient(#DADCE0 1px, transparent 1px), linear-gradient(90deg, #DADCE0 1px, transparent 1px)", backgroundSize: "40px 40px", opacity: 0.5 }} />
                            <span className="material-icons" style={{ position: "absolute", left: "50%", top: "42%", transform: "translate(-50%, -100%)", fontSize: 44, color: "#D93025" }}>location_on</span>
                            <Badge $tom="cinza" style={{ position: "absolute", left: 12, bottom: 12, background: "#fff", color: "#3C4043" }}>
                              {formData.latitude && formData.longitude
                                ? `${formData.latitude.toFixed(5)}, ${formData.longitude.toFixed(5)}`
                                : "Mapa disponível após carregar as coordenadas"}
                            </Badge>
                          </div>
                          <div style={{ display: "flex", flexDirection: "column", gap: 10, justifyContent: "center" }}>
                            <Botao type="button" $variante="tonal" onClick={() => setShowMapPicker(true)}>
                              <span className="material-icons">edit_location_alt</span>
                              Ajustar pin no mapa
                            </Botao>
                            <Hint>
                              {formData.latitude && formData.longitude
                                ? "Coordenadas salvas. Abra o mapa para conferir ou mover o pin."
                                : "Sem coordenadas salvas. Abra o mapa para fixar o pin e melhorar as rotas."}
                            </Hint>
                          </div>
                        </div>
                      )}
                    </FormGroup>
                  </FormSection>

                  <FormSection style={{ maxWidth: "none" }}>
                    <CabecalhoDaSecao
                      titulo={<>Área de cobertura<FieldSyncBadge kind="backend" /></>}
                      apoio="Cidades e bairros que você atende. Máximo de 20 áreas no Google."
                      extra={<Badge>{`${(formData.serviceAreas || []).length} de 20 áreas`}</Badge>}
                    />
                    <div className="grade-celular" style={{ display: "grid", gridTemplateColumns: "minmax(0, 1fr) 160px 140px", gap: 12, marginBottom: 18 }}>
                      <Campo>
                        <span className="material-icons">search</span>
                        <input
                          aria-label="Area de pesquisa"
                          value={coverageAreaSearchQuery}
                          onChange={(e) => setCoverageAreaSearchQuery(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === "Enter") {
                              e.preventDefault();
                              addTypedServiceArea();
                            }
                          }}
                          placeholder="Buscar cidade ou bairro"
                        />
                      </Campo>
                      <Campo>
                        <select aria-label="Raio de cobertura" value={coverageAreaRadiusKm} onChange={(e) => setCoverageAreaRadiusKm(Number(e.target.value))}>
                          <option value={2}>Raio 2 km</option>
                          <option value={5}>Raio 5 km</option>
                          <option value={10}>Raio 10 km</option>
                          <option value={15}>Raio 15 km</option>
                        </select>
                        <span className="material-icons">expand_more</span>
                      </Campo>
                      <Botao type="button" $variante="filled" style={{ height: 48 }} onClick={addTypedServiceArea} disabled={!coverageAreaSearchQuery.trim()}>
                        <span className="material-icons">add</span>
                        Adicionar
                      </Botao>
                    </div>

                    <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 18 }}>
                      {(formData.serviceAreas || []).map((area) => (
                        <Chip key={area}>
                          <span className="texto">{area}</span>
                          <ChipX type="button" aria-label={`Remover área ${area}`} onClick={() => removeServiceArea(area)}>
                            <span className="material-icons">close</span>
                          </ChipX>
                        </Chip>
                      ))}
                      {(formData.serviceAreas || []).length === 0 && <Hint>Nenhuma área selecionada</Hint>}
                    </div>
                    {autoFilledAreaProfileId === formData.id && (formData.serviceAreas || []).length > 0 && (
                      <Tip $tom="aviso" style={{ marginBottom: 18 }}>
                        <span className="material-icons">info</span>
                        Áreas próximas foram pré-preenchidas pela região do perfil. Revise antes de salvar.
                      </Tip>
                    )}

                    {(serviceAreaSuggestionsLoading || serviceAreaSuggestions.length > 0) && (
                      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12 }}>
                          <Hint style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                            <span className="material-icons" style={{ fontSize: 16, color: "#1264C7" }}>auto_awesome</span>
                            {`Sugestões pelo raio de ${coverageAreaRadiusKm} km`}
                          </Hint>
                          <BotaoPequeno
                            type="button"
                            $variante="outlined"
                            disabled={serviceAreaSuggestionsLoading || serviceAreaSuggestions.length === 0}
                            onClick={fillSuggestedServiceAreas}
                          >
                            <span className="material-icons">add_location_alt</span>
                            Preencher areas
                          </BotaoPequeno>
                        </div>
                        {serviceAreaSuggestionsLoading ? (
                          <Hint>Buscando bairros dentro do raio...</Hint>
                        ) : (
                          <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                            {serviceAreaSuggestions.map((suggestion) => (
                              <ChipSugestao key={suggestion.label} type="button" onClick={() => addServiceArea(suggestion.label)}>
                                <span className="material-icons">add</span>
                                {suggestion.label}
                                <span style={{ color: "#5F6368", fontWeight: 400 }}>
                                  {` · ${suggestion.distanceKm.toLocaleString("pt-BR", { maximumFractionDigits: 1, minimumFractionDigits: 0 })} km`}
                                </span>
                              </ChipSugestao>
                            ))}
                          </div>
                        )}
                      </div>
                    )}
                  </FormSection>
                </Coluna>
                <Aside>
                  <AsideCard style={{ gap: 14 }}>
                    <Lab>Como aparece no Google</Lab>
                    <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                      <span style={{ fontFamily: "var(--font-sans)", fontSize: 16, fontWeight: 500 }}>{formData.name || "Nome da empresa"}</span>
                      <span style={{ fontSize: 13, color: "#3C4043", display: "flex", gap: 6 }}>
                        <span className="material-icons" style={{ fontSize: 18, color: "#5F6368" }}>location_on</span>
                        {[formData.address, [formData.city, formData.state].filter(Boolean).join(", "), formData.zipCode].filter(Boolean).join(" · ") || "Endereço não informado"}
                      </span>
                      <span style={{ fontSize: 13, color: "#3C4043", display: "flex", gap: 6 }}>
                        <span className="material-icons" style={{ fontSize: 18, color: "#5F6368" }}>explore</span>
                        {(formData.serviceAreas || []).length === 0
                          ? "Sem áreas de cobertura cadastradas"
                          : `Atende ${formData.serviceAreas?.[0]}${(formData.serviceAreas || []).length > 1 ? ` e mais ${(formData.serviceAreas || []).length - 1} ${(formData.serviceAreas || []).length === 2 ? "área" : "áreas"}` : ""}`}
                      </span>
                    </div>
                  </AsideCard>
                  <Tip>
                    <span className="material-icons">lightbulb</span>
                    Mantenha cadastradas apenas as áreas em que a empresa realmente atende. Isso ajuda os clientes a encontrarem você nas buscas locais.
                  </Tip>
                </Aside>
              </EditorGrid>
            )}

            {activeTab === "horarios" && (
              <FormSection>
                <SectionTitle>
                  Horário de funcionamento
                  <FieldSyncBadge kind="backend" />
                </SectionTitle>
                <SectionHint>
                  Ligue o dia, adicione os intervalos e pronto. Cada intervalo tem a hora de abrir e a de fechar.
                </SectionHint>
                <HoursToolbar>
                  <HoursChip type="button" onClick={copiarSegundaParaDiasUteis}>
                    <span className="material-icons">content_copy</span>
                    Copiar segunda para dias úteis
                  </HoursChip>
                  <HoursChip type="button" onClick={aplicarHorarioComercial}>
                    <span className="material-icons">schedule</span>
                    Comercial 8h–18h
                  </HoursChip>
                </HoursToolbar>
                <HoursHeader>
                  <span>Dia</span>
                  <span>Aberto</span>
                  <span>Intervalos</span>
                </HoursHeader>
                {[
                  { day: "monday", label: "Segunda-feira" },
                  { day: "tuesday", label: "Terça-feira" },
                  { day: "wednesday", label: "Quarta-feira" },
                  { day: "thursday", label: "Quinta-feira" },
                  { day: "friday", label: "Sexta-feira" },
                  { day: "saturday", label: "Sábado" },
                  { day: "sunday", label: "Domingo" },
                ].map(({ day, label }) => {
                  const dayKey = day as
                    | "monday"
                    | "tuesday"
                    | "wednesday"
                    | "thursday"
                    | "friday"
                    | "saturday"
                    | "sunday";
                  const currentHour = formData?.hours?.[dayKey];

                  // Converter para array se necessário
                  let hoursArray: string[] = [];
                  if (typeof currentHour === "string") {
                    hoursArray =
                      currentHour.toLowerCase() === "fechado" ||
                      currentHour === ""
                        ? []
                        : [currentHour];
                  } else if (Array.isArray(currentHour)) {
                    hoursArray = (currentHour as string[]).filter(
                      (h: string) => h.toLowerCase() !== "fechado" && h !== ""
                    );
                  }

                  const isClosed = hoursArray.length === 0;

                  const addTimeSlot = () => {
                    const newSlot = "08:00 - 18:00";
                    setFormData((prev) => {
                      if (!prev) return null;
                      const currentHours = prev.hours?.[dayKey];
                      let updatedHours: string[];

                      if (typeof currentHours === "string") {
                        updatedHours =
                          currentHours.toLowerCase() === "fechado" ||
                          currentHours === ""
                            ? [newSlot]
                            : [currentHours, newSlot];
                      } else if (Array.isArray(currentHours)) {
                        updatedHours = [...currentHours, newSlot];
                      } else {
                        updatedHours = [newSlot];
                      }

                      return {
                        ...prev,
                        hours: {
                          ...prev.hours,
                          [day]: updatedHours,
                        },
                      };
                    });
                  };

                  const removeTimeSlot = (index: number) => {
                    setFormData((prev) => {
                      if (!prev) return null;
                      const currentHours = prev.hours?.[dayKey];
                      let updatedHours: string | string[];

                      if (Array.isArray(currentHours)) {
                        const filtered = currentHours.filter(
                          (_, i) => i !== index
                        );
                        updatedHours =
                          filtered.length === 0
                            ? "Fechado"
                            : filtered.length === 1
                            ? filtered[0]
                            : filtered;
                      } else {
                        updatedHours = "Fechado";
                      }

                      return {
                        ...prev,
                        hours: {
                          ...prev.hours,
                          [day]: updatedHours,
                        },
                      };
                    });
                  };

                  const updateTimeSlot = (
                    index: number,
                    openTime: string,
                    closeTime: string
                  ) => {
                    const newValue = `${openTime} - ${closeTime}`;
                    setFormData((prev) => {
                      if (!prev) return null;
                      const currentHours = prev.hours?.[dayKey];
                      let updatedHours: string | string[];

                      if (Array.isArray(currentHours)) {
                        const updated = [...currentHours];
                        updated[index] = newValue;
                        updatedHours =
                          updated.length === 1 ? updated[0] : updated;
                      } else {
                        updatedHours = newValue;
                      }

                      return {
                        ...prev,
                        hours: {
                          ...prev.hours,
                          [day]: updatedHours,
                        },
                      };
                    });
                  };

                  const setDay = (value: string | string[]) =>
                    setFormData((prev) =>
                      prev ? { ...prev, hours: { ...prev.hours, [day]: value } } : null,
                    );
                  return (
                    <HoursDayRow key={day} $closed={isClosed}>
                      <span className="day">{label}</span>
                      <HoursSwitch
                        type="button"
                        role="switch"
                        aria-checked={!isClosed}
                        aria-label={`Aberto: ${label}`}
                        title={isClosed ? "Abrir neste dia" : "Fechar neste dia"}
                        $on={!isClosed}
                        onClick={() => setDay(isClosed ? "08:00 - 18:00" : "Fechado")}
                      />
                      <div className="slots">
                        {hoursArray.map((hourRange, index) => {
                          const [openTime, closeTime] = hourRange.includes("-")
                            ? hourRange.split("-").map((t) => t.trim())
                            : ["08:00", "18:00"];
                          return (
                            <HoursSlot key={index}>
                              <input
                                type="time"
                                value={openTime}
                                aria-label={`${label}: abre às`}
                                onChange={(e) =>
                                  updateTimeSlot(index, e.target.value || openTime, closeTime)
                                }
                              />
                              <span aria-hidden="true">–</span>
                              <input
                                type="time"
                                value={closeTime}
                                aria-label={`${label}: fecha às`}
                                onChange={(e) =>
                                  updateTimeSlot(index, openTime, e.target.value || closeTime)
                                }
                              />
                              <button
                                type="button"
                                className="x"
                                onClick={() => removeTimeSlot(index)}
                                aria-label={`Remover intervalo de ${label}`}
                                title="Remover intervalo"
                              >
                                <span className="material-icons">close</span>
                              </button>
                            </HoursSlot>
                          );
                        })}
                        {isClosed ? (
                          <span className="closed">Fechado</span>
                        ) : (
                          <AddSlotButton type="button" onClick={addTimeSlot}>
                            <span className="material-icons">add</span>
                            Intervalo
                          </AddSlotButton>
                        )}
                      </div>
                    </HoursDayRow>
                  );
                })}
                <SectionHint style={{ margin: "12px 0 24px" }}>
                  Dois intervalos no mesmo dia servem para pausa de almoço (ex.: 08:00–12:00 e 14:00–18:00).
                </SectionHint>
                {/* Horários Especiais */}
                <SectionTitle style={{ marginTop: "32px" }}>
                  Horários especiais
                  <FieldSyncBadge kind="backend" />
                  <Tooltip
                    title="Confirme as datas comemorativas ou adicione o horário de funcionamento para que os clientes saibam quando sua empresa está aberta"
                    arrow
                    placement="top"
                  >
                    <InfoIcon className="material-icons">info</InfoIcon>
                  </Tooltip>
                </SectionTitle>

                <div
                  style={{
                    marginBottom: "16px",
                    padding: "12px 16px",
                    background: "#fffbeb",
                    border: "1px solid #fde68a",
                    borderRadius: "8px",
                    fontSize: "13px",
                    color: "#92400e",
                    lineHeight: "1.5",
                  }}
                >
                  Confirme as datas comemorativas ou adicione o horário de
                  funcionamento para que os clientes saibam quando sua empresa
                  está aberta.
                  <a
                    href="#"
                    style={{
                      color: "#1264C7",
                      textDecoration: "none",
                      fontWeight: 500,
                      marginLeft: "4px",
                    }}
                  >
                    Saiba mais
                  </a>
                </div>

                {(formData.specialHours || []).map((specialHour, index) => {
                  const [openTime, closeTime] = specialHour.hours?.includes("-")
                    ? specialHour.hours.split("-").map((t) => t.trim())
                    : ["", ""];

                  return (
                    <div
                      key={index}
                      style={{
                        marginBottom: "16px",
                        padding: "16px",
                        background: "#ffffff",
                        borderRadius: "8px",
                        border: "1px solid #e5e7eb",
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "flex-start",
                          marginBottom: "12px",
                        }}
                      >
                        <div style={{ flex: 1 }}>
                          <div
                            style={{
                              fontSize: "14px",
                              fontWeight: 600,
                              color: "#111827",
                              marginBottom: "4px",
                            }}
                          >
                            {specialHour.date
                              ? new Date(specialHour.date).toLocaleDateString(
                                  "pt-BR",
                                  {
                                    day: "2-digit",
                                    month: "short",
                                    year: "numeric",
                                  }
                                )
                              : "Nova data"}
                          </div>
                          <Input
                            type="date"
                            value={specialHour.date}
                            onChange={(e) => {
                              setFormData((prev) => {
                                if (!prev) return null;
                                const updated = [...(prev.specialHours || [])];
                                updated[index] = {
                                  ...updated[index],
                                  date: e.target.value,
                                };
                                return { ...prev, specialHours: updated };
                              });
                            }}
                            style={{
                              marginTop: "8px",
                              padding: "8px 12px",
                              fontSize: "14px",
                            }}
                          />
                        </div>
                        <button
                          type="button"
                          onClick={() => {
                            setFormData((prev) => {
                              if (!prev) return null;
                              const updated = (prev.specialHours || []).filter(
                                (_, i) => i !== index
                              );
                              return { ...prev, specialHours: updated };
                            });
                          }}
                          style={{
                            marginLeft: "12px",
                            padding: "8px",
                            background: "transparent",
                            border: "none",
                            cursor: "pointer",
                            color: "#6b7280",
                            fontSize: "14px",
                            fontWeight: 500,
                            transition: "color 0.2s",
                          }}
                          onMouseOver={(e) =>
                            (e.currentTarget.style.color = "#1264C7")
                          }
                          onMouseOut={(e) =>
                            (e.currentTarget.style.color = "#6b7280")
                          }
                        >
                          Editar
                        </button>
                      </div>

                      <div
                        style={{
                          display: "flex",
                          gap: "12px",
                          alignItems: "center",
                        }}
                      >
                        <div style={{ flex: 1 }}>
                          <label
                            style={{
                              display: "block",
                              fontSize: "12px",
                              color: "#6b7280",
                              marginBottom: "4px",
                            }}
                          >
                            Abre às(s)
                          </label>
                          <TimePickerCustom
                            value={openTime}
                            onChange={(value) => {
                              const newHours = `${value} - ${closeTime}`;
                              setFormData((prev) => {
                                if (!prev) return null;
                                const updated = [...(prev.specialHours || [])];
                                updated[index] = {
                                  ...updated[index],
                                  hours: newHours,
                                };
                                return { ...prev, specialHours: updated };
                              });
                            }}
                          />
                        </div>

                        <div style={{ flex: 1 }}>
                          <label
                            style={{
                              display: "block",
                              fontSize: "12px",
                              color: "#6b7280",
                              marginBottom: "4px",
                            }}
                          >
                            Fecha às(s)
                          </label>
                          <TimePickerCustom
                            value={closeTime}
                            onChange={(value) => {
                              const newHours = `${openTime} - ${value}`;
                              setFormData((prev) => {
                                if (!prev) return null;
                                const updated = [...(prev.specialHours || [])];
                                updated[index] = {
                                  ...updated[index],
                                  hours: newHours,
                                };
                                return { ...prev, specialHours: updated };
                              });
                            }}
                          />
                        </div>
                      </div>
                    </div>
                  );
                })}

                <button
                  type="button"
                  onClick={() => {
                    setFormData((prev) => {
                      if (!prev) return null;
                      const newSpecialHour = {
                        date: "",
                        hours: "08:00 - 18:00",
                      };
                      return {
                        ...prev,
                        specialHours: [
                          ...(prev.specialHours || []),
                          newSpecialHour,
                        ],
                      };
                    });
                  }}
                  style={{
                    width: "100%",
                    padding: "12px",
                    background: "#ffffff",
                    border: "2px dashed #d1d5db",
                    borderRadius: "8px",
                    color: "#1264C7",
                    fontSize: "14px",
                    fontWeight: 500,
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: "8px",
                    transition: "all 0.2s",
                  }}
                  onMouseOver={(e) => {
                    e.currentTarget.style.background = "#f0f9ff";
                    e.currentTarget.style.borderColor = "#1264C7";
                  }}
                  onMouseOut={(e) => {
                    e.currentTarget.style.background = "#ffffff";
                    e.currentTarget.style.borderColor = "#d1d5db";
                  }}
                >
                  <span className="material-icons" style={{ fontSize: "20px" }}>
                    add
                  </span>
                  Adicionar data
                </button>
              </FormSection>
            )}

            {activeTab === "descricao" && (
              <EditorGrid>
                <Coluna>
                  <FormSection style={{ maxWidth: "none" }}>
                    <CabecalhoDaSecao
                      titulo={<>Descrição do negócio<FieldSyncBadge kind="backend" /></>}
                      apoio='Até 2.000 caracteres. O Google mostra as primeiras 250 letras e esconde o resto atrás de "mais".'
                      extra={
                        <Badge $tom={(formData.description?.length || 0) > 2000 ? "amarelo" : "cinza"}>
                          {`${(formData.description?.length || 0).toLocaleString("pt-BR")} / 2.000`}
                        </Badge>
                      }
                    />
                    <FormGroup style={{ marginBottom: 0 }}>
                      <Label htmlFor="description">
                        <span className="material-icons">description</span>
                        Descrição detalhada
                        <FieldSyncBadge kind="backend" />
                      </Label>
                      <TextArea
                        id="description"
                        name="description"
                        value={formData.description || ""}
                        onChange={handleChange}
                        placeholder="Conte aos clientes sobre seu negócio, os serviços/produtos que oferece, seus diferenciais e o que torna sua empresa especial..."
                        maxLength={2000}
                        style={{ minHeight: 240 }}
                      />
                      <CharCounter $overLimit={(formData.description?.length || 0) > 2000}>
                        {formData.description?.length || 0}/2000 caracteres
                      </CharCounter>
                    </FormGroup>
                  </FormSection>
                </Coluna>
                <Aside>
                  <AsideCard>
                    <Lab>Como aparece no Google</Lab>
                    <div style={{ display: "flex", flexDirection: "column", gap: 6, padding: 14, borderRadius: 12, background: "#F7F9FC" }}>
                      <span style={{ fontFamily: "var(--font-sans)", fontSize: 15, fontWeight: 500 }}>Sobre</span>
                      <p style={{ margin: 0, fontSize: 13, lineHeight: "19px", color: "#2B2D31", overflowWrap: "anywhere" }}>
                        {(formData.description || "").trim()
                          ? (
                            <>
                              {(formData.description || "").slice(0, 250)}
                              {(formData.description || "").length > 250 && <>… <span style={{ color: "#1264C7" }}>mais</span></>}
                            </>
                          )
                          : "Sem descrição cadastrada."}
                      </p>
                    </div>
                    {(formData.description || "").length > 250 && (
                      <Hint>O corte em 250 letras acontece em "…{(formData.description || "").slice(230, 250).trim()}". Considere mover a frase mais importante para o início.</Hint>
                    )}
                  </AsideCard>
                  <AsideCard style={{ gap: 10 }}>
                    <Lab>Regras do Google e boas práticas</Lab>
                    {(() => {
                      const texto = (formData.description || "").toLowerCase();
                      const cidade = (formData.city || "").split(",")[0].trim().toLowerCase();
                      const categoria = (formData.category || "").toLowerCase().split(" ").filter((w) => w.length > 3);
                      const regras = [
                        { rotulo: "Cita a cidade e a região atendida", ok: Boolean(cidade) && texto.includes(cidade) },
                        { rotulo: "Cita a categoria principal", ok: categoria.some((w) => texto.includes(w)) },
                        { rotulo: "Sem links, telefones ou promoções", ok: texto.length > 0 && !/(https?:\/\/|www\.|\(\d{2}\)\s?\d|\d{4,5}-\d{4}|promo[cç][aã]o|desconto)/i.test(texto) },
                        { rotulo: "Frase principal nas primeiras 250 letras", ok: texto.length >= 80 },
                        { rotulo: "Dentro do limite de 2.000 caracteres", ok: texto.length > 0 && texto.length <= 2000 },
                      ];
                      return regras.map((regra) => (
                        <Check key={regra.rotulo} $ok={regra.ok}>
                          <span className="material-icons">{regra.ok ? "check_circle" : "radio_button_unchecked"}</span>
                          {regra.rotulo}
                        </Check>
                      ));
                    })()}
                  </AsideCard>
                  <Tip>
                    <span className="material-icons">lightbulb</span>
                    Recomendamos citar categoria, região e serviços com clareza, dentro do limite de 2.000 caracteres e das regras do Google (sem links, telefones ou promoções).
                  </Tip>
                </Aside>
              </EditorGrid>
            )}

            {activeTab === "servicos" && (
              <ServicesTab profile={formData} />
            )}

            {activeTab === "produtos" && (
              <ProductsTab profile={formData} />
            )}



            {activeTab === "postagens" && (
              <PostsTab profile={formData} />
            )}

            {activeTab === "instagram" && INSTAGRAM_TAB_ENABLED && isInternalUser && (
              <InstagramTab profile={formData} />
            )}

            {activeTab === "gabarito" && (
              <GabaritoTab profile={formData} />
            )}

            {activeTab === "avaliacoes" && isInternalUser && (
              <ReviewsManager profileId={formData.id} canEdit embedded />
            )}

            {activeTab === "presenca" && (
              <PresencaDigitalTab profile={profile ?? formData} />
            )}

            {activeTab === "seo" && (
              <SeoTab profile={formData} />
            )}

            {activeTab === "atributos" && (
              <EditorGrid>
                <Coluna style={{ gap: 18 }}>
                  <CabecalhoDaSecao
                    titulo={<>Mais atributos<FieldSyncBadge kind="backend" /></>}
                    apoio='Detalhes que o Google mostra como "Acessibilidade", "Pagamentos" e "Comodidades". Marque o que a empresa oferece; em branco não aparece.'
                    extra={
                      <Badge>
                        <span className="material-icons">checklist</span>
                        {`${atributosPreenchidos} de ${TOTAL_DE_ATRIBUTOS} atributos preenchidos`}
                      </Badge>
                    }
                  />
                  <Campo style={{ width: 360, maxWidth: "100%", height: 44, marginTop: -8 }}>
                    <span className="material-icons">search</span>
                    <input
                      type="search"
                      placeholder="Buscar atributo, ex.: Wi-Fi"
                      aria-label="Buscar atributo"
                      value={buscaAtributo}
                      onChange={(e) => setBuscaAtributo(e.target.value)}
                    />
                  </Campo>
                  <div style={{ background: "#fff", border: "1px solid var(--line)", borderRadius: 16, overflow: "hidden" }}>
                    {GRUPOS_DE_ATRIBUTOS.map((grupo) => {
                      const marcados = (formData[grupo.chave] || []).filter((i) => grupo.itens.includes(i));
                      const itensVisiveis = filtroDeAtributo
                        ? grupo.itens.filter((item) => item.toLowerCase().includes(filtroDeAtributo))
                        : grupo.itens;
                      if (filtroDeAtributo && itensVisiveis.length === 0) return null;
                      const aberto = filtroDeAtributo ? true : grupoAberto === grupo.chave;
                      return (
                        <div key={grupo.chave}>
                          <button
                            type="button"
                            aria-expanded={aberto}
                            onClick={() => setGrupoAberto((atual) => (atual === grupo.chave ? null : grupo.chave))}
                            style={{ display: "flex", alignItems: "center", gap: 12, height: 56, padding: "0 18px", width: "100%", border: 0, borderBottom: "1px solid #EEF1F4", background: "#fff", cursor: "pointer", textAlign: "left", font: "inherit" }}
                          >
                            <span className="material-icons" style={{ width: 36, height: 36, borderRadius: 10, background: "#EAF1FB", color: "#0B4FA8", display: "inline-flex", alignItems: "center", justifyContent: "center", fontSize: 20 }}>{grupo.icone}</span>
                            <span style={{ fontFamily: "var(--font-sans)", fontSize: 15, fontWeight: 600, color: "#1F1F1F" }}>{grupo.nome}</span>
                            <Badge $tom="cinza">{`${marcados.length} de ${grupo.itens.length}`}</Badge>
                            <span style={{ flex: 1 }} />
                            <span className="material-icons" style={{ color: "#5F6368" }}>{aberto ? "expand_less" : "expand_more"}</span>
                          </button>
                          {aberto && (
                            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))", gap: "2px 24px", padding: "8px 18px 16px 66px", borderBottom: "1px solid #EEF1F4" }}>
                              {itensVisiveis.map((item) => {
                                const marcado = marcados.includes(item);
                                return (
                                  <div key={item} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, height: 44, fontSize: 14, color: "#2B2D31" }}>
                                    <span>{item}</span>
                                    <div role="group" aria-label={item} style={{ display: "inline-flex", border: "1px solid #E2E6EA", borderRadius: 10, overflow: "hidden" }}>
                                      <button
                                        type="button"
                                        aria-pressed={marcado}
                                        onClick={() => alternarAtributo(grupo.chave, item, true)}
                                        style={{ height: 32, padding: "0 12px", border: 0, background: marcado ? "#EAF7EE" : "#fff", color: marcado ? "#137333" : "#5F6368", fontFamily: "var(--font-sans)", fontSize: 12, fontWeight: 500, cursor: "pointer" }}
                                      >
                                        Sim
                                      </button>
                                      <button
                                        type="button"
                                        aria-pressed={!marcado}
                                        aria-label={`${item}: não informar`}
                                        onClick={() => alternarAtributo(grupo.chave, item, false)}
                                        style={{ height: 32, padding: "0 12px", border: 0, borderLeft: "1px solid #E2E6EA", background: "#fff", color: "#5F6368", fontFamily: "var(--font-sans)", fontSize: 12, fontWeight: 500, cursor: "pointer" }}
                                      >
                                        —
                                      </button>
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          )}
                        </div>
                      );
                    })}
                    {filtroDeAtributo && GRUPOS_DE_ATRIBUTOS.every((grupo) => !grupo.itens.some((item) => item.toLowerCase().includes(filtroDeAtributo))) && (
                      <Hint style={{ display: "block", padding: 18 }}>Nenhum atributo com esse nome.</Hint>
                    )}
                  </div>
                </Coluna>
                <Aside>
                  <AsideCard>
                    <Lab>Como aparece no Google</Lab>
                    <div style={{ display: "flex", flexDirection: "column", gap: 10, fontSize: 13, color: "#2B2D31" }}>
                      {GRUPOS_DE_ATRIBUTOS.map((grupo) => {
                        const marcados = (formData[grupo.chave] || []).filter((i) => grupo.itens.includes(i));
                        return (
                          <div key={grupo.chave}>
                            <span style={{ fontFamily: "var(--font-sans)", fontWeight: 500 }}>{grupo.nome}</span>
                            <br />
                            {marcados.length > 0 ? marcados.join(" · ") : <span style={{ color: "#9AA0A6" }}>Nada informado</span>}
                          </div>
                        );
                      })}
                    </div>
                  </AsideCard>
                  <Tip>
                    <span className="material-icons">info</span>
                    Atributos ajudam clientes a entender rapidamente estrutura, acessibilidade e formas de atendimento. Só o que estiver marcado aparece na ficha.
                  </Tip>
                </Aside>
              </EditorGrid>
            )}

            {activeTab === "ia" && (
              <EditorGrid style={{ gridTemplateColumns: "minmax(0, 8fr) minmax(0, 4fr)" }}>
                <Coluna style={{ gap: 20 }}>
                  <CabecalhoDaSecao
                    titulo={<><span className="material-icons" style={{ color: "#1264C7" }}>auto_awesome</span>Assistente IA e SEO<FieldSyncBadge kind="ia" /></>}
                    apoio="Diga o que você quer vender ou destacar. A IA analisa o perfil, o Gabarito e a região e sugere o que mudar. Nada é publicado sem sua confirmação."
                    extra={
                      <Badge $tom="verde">
                        <span className="material-icons">verified_user</span>
                        Nada será publicado sem sua confirmação
                      </Badge>
                    }
                  />

                  <div style={{ background: "#fff", border: "1px solid var(--line)", borderRadius: 16, padding: 22, display: "flex", flexDirection: "column", gap: 14 }}>
                    <label htmlFor="ia-description" style={{ fontFamily: "var(--font-sans)", fontSize: 15, fontWeight: 600, color: "#1F1F1F" }}>O que você quer vender ou destacar?</label>
                    <div style={{ display: "flex", alignItems: "flex-start", gap: 10, padding: "12px 14px", border: "1px solid #D4D9DE", borderRadius: 12, background: "#fff" }}>
                      <span className="material-icons" style={{ color: "#5F6368", paddingTop: 2 }}>edit_note</span>
                      <textarea
                        id="ia-description"
                        name="ia-description"
                        rows={3}
                        value={iaDescription}
                        onChange={(e) => setIaDescription(e.target.value)}
                        placeholder="Ex: Restaurante italiano familiar com pratos tradicionais, localização central..."
                        style={{ border: 0, outline: 0, flex: 1, font: "15px/24px inherit", fontFamily: "inherit", color: "#1F1F1F", background: "transparent", minWidth: 0, padding: 0, resize: "vertical" }}
                      />
                    </div>
                    <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                      <Hint>Ações:</Hint>
                      <BotaoPequeno type="button" $variante="outlined" onClick={handleSuggestKeywordsWithContext} disabled={iaLoadingAction !== null}>
                        <span className="material-icons">refresh</span>
                        {iaLoadingAction === "keywords" ? "Processando..." : "Sugerir palavras-chave"}
                      </BotaoPequeno>
                      <BotaoPequeno type="button" $variante="outlined" onClick={handleSuggestCategoriesWithContext} disabled={iaLoadingAction !== null}>
                        <span className="material-icons">category</span>
                        {iaLoadingAction === "categories" ? "Processando..." : "Sugerir categorias"}
                      </BotaoPequeno>
                      <BotaoPequeno type="button" $variante="outlined" onClick={handleGenerateBriefingWithContext} disabled={iaLoadingAction !== null}>
                        <span className="material-icons">tune</span>
                        {iaLoadingAction === "briefing" ? "Processando..." : "Gerar descrição"}
                      </BotaoPequeno>
                      <span style={{ flex: 1 }} />
                      <Botao type="button" $variante="filled" onClick={handleGenerateCommercialPlan} disabled={iaLoadingAction !== null}>
                        <span className="material-icons">auto_awesome</span>
                        {iaLoadingAction === "commercial" ? "Mapeando..." : "Gerar plano comercial para SEO"}
                      </Botao>
                    </div>
                    <Hint>A IA usa o nome, o local, a categoria atual, o contexto escrito aqui e as palavras do Gabarito. Nome e categoria principal nunca são alterados.</Hint>
                    {iaFeedback && (
                      <div
                        role="status"
                        style={{
                          padding: "12px 14px",
                          borderRadius: 12,
                          fontSize: 14,
                          fontWeight: 500,
                          background: iaFeedback.type === "error" ? "#FCE8E6" : iaFeedback.type === "success" ? "#EAF7EE" : "#EAF1FB",
                          color: iaFeedback.type === "error" ? "#B3261E" : iaFeedback.type === "success" ? "#137333" : "#0B4FA8",
                        }}
                      >
                        {iaFeedback.message}
                      </div>
                    )}
                  </div>

                  {commercialPlan && (
                    <div style={{ background: "#fff", border: "1px solid var(--line)", borderRadius: 16, padding: 22, display: "flex", flexDirection: "column", gap: 14 }}>
                      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, flexWrap: "wrap" }}>
                        <span style={{ fontFamily: "var(--font-sans)", fontSize: 15, fontWeight: 600 }}>Perfil analisado</span>
                        <Badge $tom="cinza">
                          <span className="material-icons">sync</span>
                          Fonte: Google Business Profile
                        </Badge>
                      </div>
                      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))", gap: 10 }}>
                        {[
                          { rotulo: "Empresa", valor: formData.name },
                          { rotulo: "Segmento", valor: formData.category },
                          { rotulo: "Local", valor: [formData.city, formData.state].filter(Boolean).join(" · ") },
                          { rotulo: "Site", valor: (formData.website || "").replace(/^https?:\/\//i, "") || "—" },
                        ].map((kv) => (
                          <div key={kv.rotulo} style={{ display: "flex", flexDirection: "column", gap: 3, padding: "12px 14px", borderRadius: 10, background: "#F7F9FC", minWidth: 0 }}>
                            <Lab style={{ fontSize: 11, letterSpacing: "0.6px", color: "#80868B" }}>{kv.rotulo}</Lab>
                            <b style={{ fontWeight: 500, fontSize: 14, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{kv.valor || "—"}</b>
                          </div>
                        ))}
                      </div>
                      {commercialPlan.diagnostico && (
                        <div style={{ padding: "14px 16px", borderRadius: 12, background: "#EAF1FB", fontSize: 14, lineHeight: "21px", color: "#1F1F1F" }}>
                          <b style={{ fontWeight: 500 }}>Diagnóstico da pesquisa. </b>
                          {commercialPlan.diagnostico}
                        </div>
                      )}
                      <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                        {commercialPlan.grupos.flatMap((grupo) => grupo.keywords).slice(0, 12).map((termo) => (
                          <Badge key={termo} $tom="cinza">{termo}</Badge>
                        ))}
                      </div>
                      <Hint>
                        {`${commercialPlan.itens.filter((item) => item.uso === "servico").length} serviços, ${commercialPlan.produtos.length + commercialPlan.itens.filter((item) => item.uso === "produto").length} produtos e ${keywordDrafts.length} palavras com intenção comercial. A aba SEO usa exatamente este plano.`}
                      </Hint>
                      <BotaoPequeno type="button" $variante="tonal" style={{ alignSelf: "flex-start" }} onClick={() => setActiveTab("seo")}>
                        <span className="material-icons">travel_explore</span>
                        Revisar e publicar na aba SEO
                      </BotaoPequeno>
                    </div>
                  )}

                  {keywordDrafts.length > 0 && (
                    <div style={{ background: "#fff", border: "1px solid var(--line)", borderRadius: 16, overflow: "hidden" }}>
                      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, padding: "14px 18px", borderBottom: "1px solid var(--line)", background: "#F8FAFC" }}>
                        <span style={{ fontFamily: "var(--font-sans)", fontSize: 14, fontWeight: 600 }}>Palavras-chave sugeridas</span>
                        <Hint>{`${keywordDrafts.length} sugestões · ${selectedKeywordValues.length} marcadas`}</Hint>
                      </div>
                      <div style={{ padding: "8px 18px" }}>
                        <Hint style={{ display: "block", padding: "6px 0 10px" }}>Revise, corrija ou desmarque antes de aplicar. Nada vai para o Gabarito sem essa confirmação.</Hint>
                        <div style={{ display: "grid", gap: 8 }}>
                          {keywordDrafts.map((keyword) => (
                            <div key={keyword.id} style={{ alignItems: "center", display: "grid", gap: 10, gridTemplateColumns: "24px 1fr 32px" }}>
                              <input
                                type="checkbox"
                                aria-label={`Usar ${keyword.value}`}
                                checked={keyword.selected}
                                onChange={(event) => setKeywordDrafts((prev) => updateKeywordDraftSelection(prev, keyword.id, event.target.checked))}
                                style={{ accentColor: "#1264C7", height: 18, width: 18 }}
                              />
                              <Input
                                value={keyword.value}
                                onChange={(event) => setKeywordDrafts((prev) => updateKeywordDraftValue(prev, keyword.id, event.target.value))}
                                style={{ height: 40 }}
                              />
                              <button
                                type="button"
                                aria-label={`Remover ${keyword.value}`}
                                onClick={() => setKeywordDrafts((prev) => removeKeywordDraft(prev, keyword.id))}
                                style={{ alignItems: "center", background: "transparent", border: "none", color: "#5F6368", cursor: "pointer", display: "inline-flex", justifyContent: "center", padding: 4 }}
                              >
                                <span className="material-icons" style={{ fontSize: 18 }}>close</span>
                              </button>
                            </div>
                          ))}
                        </div>
                        <Botao type="button" $variante="filled" style={{ margin: "14px 0 10px" }} onClick={handleApplyKeywordsToGabarito} disabled={selectedKeywordValues.length === 0}>
                          <span className="material-icons">assignment</span>
                          Aplicar selecionadas no Gabarito
                        </Botao>
                      </div>
                    </div>
                  )}
                </Coluna>
                <Aside>
                  <AsideCard>
                    <span style={{ fontFamily: "var(--font-sans)", fontSize: 15, fontWeight: 600, display: "flex", alignItems: "center", gap: 8 }}>
                      <span className="material-icons" style={{ color: "#0B4FA8" }}>shield</span>
                      Controle da automação
                    </span>
                    <div style={{ display: "grid", gridTemplateColumns: "repeat(2, minmax(0, 1fr))", gap: 8 }}>
                      <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 2, padding: "12px 8px", borderRadius: 10, background: "#F7F9FC" }}>
                        <b style={{ fontFamily: "var(--font-sans)", fontSize: 22, fontWeight: 500, color: "#0B4FA8" }}>30</b>
                        <span style={{ fontSize: 11, fontWeight: 600, letterSpacing: "0.4px", color: "#5F6368", textTransform: "uppercase" }}>por dia</span>
                      </div>
                      <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 2, padding: "12px 8px", borderRadius: 10, background: "#F7F9FC" }}>
                        <b style={{ fontFamily: "var(--font-sans)", fontSize: 22, fontWeight: 500, color: "#0B4FA8" }}>{palavrasDoGabarito.length}</b>
                        <span style={{ fontSize: 11, fontWeight: 600, letterSpacing: "0.4px", color: "#5F6368", textTransform: "uppercase" }}>no gabarito</span>
                      </div>
                    </div>
                    <div style={{ display: "flex", gap: 10, padding: "12px 14px", borderRadius: 12, background: "#FFF7E0", border: "1px solid #FDE293", fontSize: 13, lineHeight: "19px", color: "#5F4200" }}>
                      <span className="material-icons" style={{ color: "#B06000", flexShrink: 0, fontSize: 18 }}>info</span>
                      Nome e categoria do negócio nunca são alterados. A IA não mexe em horários, produtos ou serviços por segurança: serviços e produtos entram no plano da aba SEO, dentro do limite de 30 itens por dia.
                    </div>
                  </AsideCard>
                  <AsideCard>
                    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                      <span style={{ fontFamily: "var(--font-sans)", fontSize: 15, fontWeight: 600, display: "flex", alignItems: "center", gap: 8 }}>
                        <span className="material-icons" style={{ color: "#0B4FA8" }}>bookmark</span>
                        Palavras do Gabarito
                      </span>
                      <Badge>{`${palavrasDoGabarito.length} ${palavrasDoGabarito.length === 1 ? "palavra" : "palavras"}`}</Badge>
                    </div>
                    <Hint>A IA usa estas palavras como base das sugestões. Edite-as na aba Gabarito.</Hint>
                    <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                      {palavrasDoGabarito.slice(0, 16).map((palavra) => (
                        <Badge key={palavra}>
                          <span className="material-icons">check</span>
                          {palavra}
                        </Badge>
                      ))}
                      {palavrasDoGabarito.length === 0 && <Hint>Nenhuma palavra cadastrada ainda.</Hint>}
                    </div>
                    <BotaoPequeno type="button" $variante="tonal" style={{ alignSelf: "flex-start" }} onClick={() => setActiveTab("gabarito")}>
                      <span className="material-icons">assignment</span>
                      Abrir o Gabarito
                    </BotaoPequeno>
                  </AsideCard>
                  <AsideCard style={{ gap: 8 }}>
                    <Lab>Como funciona</Lab>
                    <Hint>1. Você descreve o que quer vender. 2. A IA propõe palavras, categorias, descrição e o plano comercial. 3. Você revisa e aprova. 4. Serviços e produtos entram no plano da aba SEO e são publicados dentro do limite diário.</Hint>
                  </AsideCard>
                </Aside>
              </EditorGrid>
            )}
          </ModalBody>

          <ModalFooter>
            <FooterInfo>
              <span className="material-icons">info</span>
              <span>
                Última atualização: {new Date().toLocaleDateString("pt-BR")}
              </span>
              {alteracoesNaoSalvas > 0 && (
                <Badge $tom="amarelo">
                  <span className="material-icons">edit</span>
                  {`${alteracoesNaoSalvas} ${alteracoesNaoSalvas === 1 ? "alteração não salva" : "alterações não salvas"}`}
                </Badge>
              )}
            </FooterInfo>
            {saveFeedback && (
              <SaveFeedback role="alert">
                <span className="material-icons">error</span>
                {saveFeedback}
              </SaveFeedback>
            )}
            <ButtonGroup>
              <Button type="button" $secondary onClick={onClose}>
                <span>Cancelar</span>
              </Button>
              <Button type="submit" disabled={loading}>
                <span className="material-icons">save</span>
                <span>{loading ? "Salvando..." : "Salvar alterações"}</span>
              </Button>
            </ButtonGroup>
          </ModalFooter>
        </form>
        </EditorLayout>
      </ModalContainer>
    </ModalOverlay>
  );
};
